<!-- eslint-disable vuejs-accessibility/aria-role -->
<template>
  <div role="radio-group" class="imagery-toggles">
    <imagery-toggle
      v-for="option in imageryOptions"
      :key="option.id"
      :toggle="option"
    />
  </div>
</template>

<script setup lang="ts">
///// imports /////
import { computed } from 'vue'
import { createUserLayer } from '@onxmaps/layer-management'
import imageryToggle from '@/fish/components/map-controls/basemap/imagery-toggle.vue'

import { saveItem } from '@/plugins/storage'

import { useLayers } from '@/services/layer-management-service/composables/use-layers'
import { toggleUserLayerActive } from '@/services/layer-management-service'
import {
  BasemapImagery,
  basemap as getBasemap,
  AERIAL,
  HYBRID,
} from '@/services/basemap-service'
import aerialHQ from '@/assets/images/basemap-fish-aerial-hq.jpg'
import aerialRecent from '@/assets/images/basemap-fish-aerial-recent.jpg'
import hybridRecent from '@/assets/images/basemap-fish-hybrid-recent.jpg'
import hybridHQ from '@/assets/images/basemap-fish-hybrid-hq.jpg'

///// refs and variables /////
interface LayerDescriptions {
  default: {
    name: string
    description: string
    image: string
    icon: string | null
  }
  highFrequency: {
    name: string
    description: string
    image: string
    icon: string | null
  }
}

const ImageryId = {
  Default: 'default',
  HighFrequency: BasemapImagery.HIGH_FREQ,
}

const imageryPerBasemap: { [key: string]: LayerDescriptions } = {
  [AERIAL]: {
    default: {
      name: 'High Quality Imagery',
      description: 'High definition satellite imagery',
      image: aerialHQ,
      icon: null,
    },
    // FUTURE - upgrade when icon available
    highFrequency: {
      name: 'Recent Satellite',
      description:
        'Scroll back in time to view recent, lower-resolution satellite imagery',
      image: aerialRecent,
      icon: 'look-back',
    },
  },

  [HYBRID]: {
    default: {
      name: 'High Quality Imagery',
      description: 'High definition satellite imagery with contours',
      image: hybridHQ,
      icon: null,
    },
    highFrequency: {
      name: 'Recent Satellite',
      description:
        'Scroll back in time to view recent, lower-resolution satellite imagery with contours',
      image: hybridRecent,
      icon: 'look-back',
    },
  },
}

const { getUserLayerById } = useLayers()

///// computed /////
const currentBasemap = computed(getBasemap)

const imageryOptions = computed(() => {
  const defaultImagery = {
    id: ImageryId.Default,
    turnImageryOn: toggleFunctions[ImageryId.Default],
    hasAccess: true,
    ...imageryPerBasemap[currentBasemap.value]['default'],
  }

  // Get user layers
  const highFrequency = createUserLayer({
    ...getUserLayerById(BasemapImagery.HIGH_FREQ)!,
    turnImageryOn: toggleFunctions[ImageryId.HighFrequency],
    ...imageryPerBasemap[currentBasemap.value]['highFrequency'],
  })
  return [defaultImagery, highFrequency]
})

///// functions /////
const toggleFunctions = {
  [ImageryId.Default]: () => {
    toggleUserLayerActive(BasemapImagery.HIGH_FREQ, false)
    saveItem('fish_freshwater_basemapImagery', 'default')
  },
  [ImageryId.HighFrequency]: () => {
    toggleUserLayerActive(BasemapImagery.HIGH_FREQ, true)
    saveItem('fish_freshwater_basemapImagery', 'highfreq')
  },
}
</script>

<style lang="css" scoped></style>
